import React, { Fragment, forwardRef, useState, useRef, useEffect, useMemo } from 'react';

import { Modal as flowbiteModal } from 'flowbite';
import { Formik } from 'formik';
import { usePopperTooltip } from 'react-popper-tooltip';
import * as Yup from 'yup';

import PurchaseRequisitionProcurementMethodModal from '../purchase_requisitions/PurchaseRequisitionProcurementMethodModal';

import { ComplexitySelector, PolicySelector, PrioritySelector } from './ProcurementEditForm';
import ProcurementExpenseTable from './ProcurementExpenseTable';

import { formatFigure } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';
import Tags from 'CommonComponents/Tags';
import TextButton from 'CommonComponents/TextButton';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useUsers } from 'Hooks/useUsers';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import PlusIcon from 'Assets/icons/plus.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function ProcurementPlanItemsTable(props) {
    // Ensure 'Unallocated' category is first
    const planItems = useMemo(() => {
        return props.planItems && props.planItems.data
            ? {
                  ...props.planItems,
                  data: [...props.planItems.data].sort((a, b) => (a.category === 'Unallocated' ? -1 : b.category === 'Unallocated' ? 1 : 0)),
              }
            : props.planItems;
    }, [props.planItems]);

    const [
        purchaseRequisitionProcurementMethodModalForProcurementEditModalElement,
        setPurchaseRequisitionProcurementMethodModalForProcurementEditModalElement,
    ] = useState(null);

    const purchaseRequisitionProcurementMethodModalForProcurementEditModalRef = useRef();

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    useEffect(() => {
        purchaseRequisitionProcurementMethodModalForProcurementEditModalRef &&
            setPurchaseRequisitionProcurementMethodModalForProcurementEditModalElement(
                new flowbiteModal(purchaseRequisitionProcurementMethodModalForProcurementEditModalRef.current, modalOptions(false)),
            );
    }, []);

    const resetData = () => {
        purchaseRequisitionProcurementMethodModalForProcurementEditModalElement &&
            purchaseRequisitionProcurementMethodModalForProcurementEditModalElement.hide();
    };

    const closeModal = () => {
        resetData();
    };

    const successHandler = () => {
        resetData();
    };

    const cleanConfirmationModalStates = () => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    };

    const showPurchaseRequisitionProcurementMethodModal = () => {
        purchaseRequisitionProcurementMethodModalForProcurementEditModalElement &&
            purchaseRequisitionProcurementMethodModalForProcurementEditModalElement.show();
    };

    return (
        <div className="inbud-table_wrapper tw-overflow-y-auto tw-max-h-screen">
            <table className="inbud-table tw-table-fixed inbud-table_first-sticky">
                <thead className="tw-sticky tw-top-0 tw-z-10">
                    <tr>
                        <th className="tw-w-80 min-[2400px]:tw-w-auto">{$t('Procurement Name')}</th>
                        <th className="tw-w-80 min-[2400px]:tw-w-auto">{$t('Description')}</th>
                        <th className="tw-w-28">{$t('Value')}</th>
                        <th className="tw-w-80">{$t('Departments')}</th>
                        <th className="tw-w-40">{$t('Owner')}</th>
                        <th className="tw-w-40">{$t('Policy')}</th>
                        <th className="tw-w-40">{$t('Priority')}</th>
                        <th className="tw-w-40">{$t('Complexity')}</th>
                        <th className="tw-w-40">{$t('Status')}</th>
                        <th className="tw-w-96">{$t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {props.isLoadingProcurements && <TableRowSkeleton columns={10} rows={5} />}
                    {!props.isLoadingProcurements && (!props.planItems?.data || !props.planItems.data.length) && (
                        <tr>
                            <td colSpan={10} className="tw-text-center tw-py-4">
                                {props.completed ? $t('No completed procurements in this plan') : $t('No procurements in this plan')}
                            </td>
                        </tr>
                    )}
                    {!props.isLoadingProcurements &&
                        planItems?.data?.map((item) => (
                            <ProcurementPlanItemsTableGroupRows
                                key={item.category}
                                items={item.items}
                                procurement={props.procurement}
                                loadingProcurement={props.loadingProcurement}
                                policies={props.policies}
                                priorities={props.priorities}
                                complexities={props.complexities}
                                heading={item.category}
                                completed={props.completed}
                                prEnabled={props.prEnabled}
                                eaEnabled={props.eaEnabled}
                                directCommitEnabled={props.directCommitEnabled}
                                partialUpdateUrl={props.partialUpdateUrl}
                                onEdit={props.onEdit}
                                onView={props.onView}
                                onDelete={props.onDelete}
                                onCreatePR={props.onCreatePR}
                                onCreatePO={props.onCreatePO}
                                onDirectCommit={props.onDirectCommit}
                                onValueHover={props.onValueHover}
                                onUpdated={props.onUpdated}
                                departmentContextId={props.departmentContextId}
                                showInfo={() => showPurchaseRequisitionProcurementMethodModal()}
                            />
                        ))}
                </tbody>
            </table>
            <PurchaseRequisitionProcurementMethodModal
                closeHandler={closeModal}
                successHandler={successHandler}
                cleanStates={cleanConfirmationModalStates}
                allowAction={true}
                modalId={'pr-method-list-edit-modal'}
                modalRef={purchaseRequisitionProcurementMethodModalForProcurementEditModalRef}
                modalElement={purchaseRequisitionProcurementMethodModalForProcurementEditModalElement}
                request={actionInProgress}
                actionCompleted={actionCompleted}
                actionFailed={actionFailed}
                title={$t('Procurement Policies')}
                cancelButtonText={$t('Close')}
                policies={props.policies}
            />
        </div>
    );
}

function ProcurementPlanItemsTableGroupRows({ items, heading, completed, ...rest }) {
    const [show, setShow] = useState(true);

    return (
        <Fragment>
            <tr className="tw-cursor-pointer" onClick={() => setShow(!show)}>
                <td className="!tw-border-r-0">
                    <div className="tw-flex tw-items-center tw-gap-x-1.5">
                        <div className="tw-font-semibold">{heading}</div>
                        {show ? <ChevronUpIcon className="tw-w-1.5 tw-h-1.5" /> : <ChevronDownIcon className="tw-w-1.5 tw-h-1.5" />}
                    </div>
                </td>
                <td colSpan={9}></td>
            </tr>
            {show &&
                items?.map((item) => (
                    <ProcurementPlanItemsTableRow key={item.id} item={item} completed={completed} {...rest} showInfo={() => rest.showInfo()} />
                ))}
        </Fragment>
    );
}

const ProcurementSchema = Yup.object().shape({
    priority: Yup.number().required($t('This value is required')),
    complexity: Yup.number(),
    owner_id: Yup.string(),
    policy_id: Yup.number().required($t('This value is required')),
});

function ProcurementPlanItemsTableRow({
    item,
    completed,
    procurement,
    loadingProcurement,
    policies,
    priorities,
    complexities,
    prEnabled,
    eaEnabled,
    directCommitEnabled,
    partialUpdateUrl,
    departmentContextId,
    onEdit,
    onView,
    onDelete,
    onCreatePR,
    onCreatePO,
    onDirectCommit,
    onValueHover,
    onUpdated,
    ...rest
}) {
    const [editable, setEditable] = useState(false);

    const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip({ interactive: true });

    const {
        getArrowProps: getDeleteArrowProps,
        getTooltipProps: getDeleteTooltipProps,
        setTooltipRef: setDeleteTooltipRef,
        setTriggerRef: setDeleteTriggerRef,
        visible: deleteTooltipVisible,
    } = usePopperTooltip({ placement: 'top' });

    const { users } = useUsers();

    // True when viewing in dept context and this item belongs entirely to another department
    const isOtherDept = !!(departmentContextId && item.is_other_dept);

    // True when viewing in dept context and this item spans multiple departments
    const isDeleteDisabled = !!(departmentContextId && item.is_multi_dept);

    const restrictNewProcurement = () => {
        // PR creation will be disabled if
        // an open PR or an approved PR already exists
        return item.pr_exists && (item.pr_open || item.pr_approved);
    };
    const isEADisabled = () => {
        return (
            // PR‐module on && (no PR OR PR not approved)
            (prEnabled && (!item.pr_exists || !item.pr_approved)) ||
            // PR‐module off && (PR exists but not approved)
            (!prEnabled && item.pr_exists && !item.pr_approved) ||
            // any PO open
            item.po_open ||
            // any PO approved
            item.po_approved
        );
    };

    // Other-dept rows are always read-only — show muted background and only allow View
    if (isOtherDept) {
        return (
            <tr className="!tw-bg-gray-50 tw-opacity-70">
                <td>{item.name}</td>
                <td>{item.description}</td>
                <td className={`font-weight-bold ${!isNaN(item.value) ? 'inbud-numeric-cell' : ''}`}>{formatFigure(item.value)}</td>
                <td>{!item.departments_names.length ? $t('No Data') : <Tags tags={item.departments_names} />}</td>
                <td>{item.owner_name || $t('No Data')}</td>
                <td>{item.policy?.method || $t('No Data')}</td>
                <td>{item.priority_label || $t('No Data')}</td>
                <td>{item.complexity_label || $t('No Data')}</td>
                <td>{item.status?.title || $t('No Data')}</td>
                <td>
                    <div className="tw-flex tw-items-center tw-gap-x-3">
                        <TextButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onView(item);
                            }}
                        >
                            <EyeIcon className="tw-w-3 tw-h-3" />
                            {$t('View')}
                        </TextButton>
                        <span
                            className="tw-text-xs tw-text-gray-400 tw-italic"
                            title={$t('This procurement belongs to another department and cannot be edited from here')}
                        >
                            {$t('Read-only')}
                        </span>
                    </div>
                </td>
            </tr>
        );
    }

    return !editable ? (
        <tr className="!tw-bg-white" onClick={() => !completed && setEditable(true)}>
            <td>{item.name}</td>
            <td>{item.description}</td>
            <td
                className={`cursor-pointer font-weight-bold ${!isNaN(item.value) ? 'inbud-numeric-cell' : ''}`}
                ref={setTriggerRef}
                onMouseEnter={() => onValueHover(item)}
            >
                {visible && (
                    <ExpensesTooltip
                        ref={setTooltipRef}
                        procurement={procurement}
                        loading={loadingProcurement}
                        getTooltipProps={getTooltipProps}
                        getArrowProps={getArrowProps}
                        completed={completed}
                    />
                )}
                {formatFigure(item.value)}
            </td>
            <td>{!item.departments_names.length ? $t('No Data') : <Tags tags={item.departments_names} />}</td>
            <td>{item.owner_name || $t('No Data')}</td>
            <td>{item.policy?.method || $t('No Data')}</td>
            <td>{item.priority_label || $t('No Data')}</td>
            <td>{item.complexity_label || $t('No Data')}</td>
            <td>{item.status?.title || $t('No Data')}</td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <TextButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(item);
                        }}
                    >
                        <EditIcon className="tw-w-3 tw-h-3" />
                        {$t('Edit')}
                    </TextButton>
                    <TextButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onView(item);
                        }}
                    >
                        <EyeIcon className="tw-w-3 tw-h-3" />
                        {$t('View')}
                    </TextButton>

                    {!completed && (
                        <>
                            {/* Wrap disabled delete in a span so tooltip triggers even when button is disabled */}
                            <span ref={isDeleteDisabled ? setDeleteTriggerRef : undefined} className="tw-inline-flex">
                                <TextButton
                                    variant="danger"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isDeleteDisabled) {
                                            onDelete(item);
                                        }
                                    }}
                                    disabled={isDeleteDisabled}
                                    style={isDeleteDisabled ? { pointerEvents: 'none' } : undefined}
                                >
                                    <TrashIcon className="tw-w-3 tw-h-3" />
                                    {$t('Delete')}
                                </TextButton>
                            </span>
                            {isDeleteDisabled && deleteTooltipVisible && (
                                <div
                                    ref={setDeleteTooltipRef}
                                    {...getDeleteTooltipProps({
                                        className: 'tw-bg-gray-800 tw-text-white tw-text-xs tw-rounded tw-px-2 tw-py-1 tw-z-[999] tw-max-w-xs',
                                    })}
                                >
                                    {$t(
                                        "Cannot delete a procurement that spans multiple departments. Remove your department's lines first, or delete centrally.",
                                    )}
                                    <div {...getDeleteArrowProps()} />
                                </div>
                            )}
                            {prEnabled && (
                                <TextButton
                                    disabled={restrictNewProcurement()}
                                    alt={$t('Create Purchase Requisition')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCreatePR(item);
                                    }}
                                >
                                    <PlusIcon className="tw-w-3 tw-h-3" />
                                    {$t('PR')}
                                </TextButton>
                            )}
                            {eaEnabled && (
                                <TextButton
                                    disabled={isEADisabled()}
                                    alt={$t('Create Expenditure Approval')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCreatePO(item);
                                    }}
                                >
                                    <PlusIcon className="tw-w-3 tw-h-3" />
                                    {$t('EA')}
                                </TextButton>
                            )}
                            {!!directCommitEnabled && (
                                <TextButton
                                    disabled={restrictNewProcurement()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDirectCommit(item);
                                    }}
                                    alt={$t('Direct Commit')}
                                >
                                    <PlusIcon className="tw-w-3 tw-h-3" />
                                    {$t('DC')}
                                </TextButton>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    ) : (
        <Formik
            enableReinitialize
            initialValues={{
                owner_id: item?.owner_id || '',
                priority: item?.priority?.toString() || '',
                complexity: item?.complexity?.toString() || '',
                policy_id: item?.policy_id || '',
            }}
            validationSchema={ProcurementSchema}
            onSubmit={(values, actions) => {
                const url = partialUpdateUrl.replace('ITEM_ID', item?.id);

                axios
                    .put(url, values)
                    .then((res) => {
                        actions.setSubmitting(false);
                        onUpdated();
                    })
                    .catch((e) => {
                        actions.setSubmitting(false);
                        e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                    });
            }}
        >
            {({ values, handleSubmit, isSubmitting, setFieldValue, resetForm }) => (
                <tr className="!tw-bg-white">
                    <td className="tw-z-10">{item.name}</td>
                    <td>{item.description}</td>
                    <td className="cursor-pointer font-weight-bold">{formatFigure(item.value)}</td>
                    <td>{!item.departments_names.length ? $t('No Data') : <Tags tags={item.departments_names} />}</td>
                    <td>
                        <UserSelectorInput selectedId={values.owner_id} usersList={users} onChange={(value) => setFieldValue('owner_id', value)} />
                    </td>
                    <td>
                        <PolicySelector hideLabel name="policy_id" policies={policies} showInfo={() => rest.showInfo()} />
                    </td>
                    <td>
                        <PrioritySelector hideLabel name="priority" priorities={priorities} />
                    </td>
                    <td>
                        <ComplexitySelector hideLabel name="complexity" complexities={complexities} />
                    </td>
                    <td>{item.status?.title || $t('No Data')}</td>
                    {!completed && (
                        <td className="tw-space-x-2">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    resetForm();
                                    setEditable(false);
                                }}
                            >
                                {$t('Cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                {$t('Save')}
                            </Button>
                        </td>
                    )}
                </tr>
            )}
        </Formik>
    );
}

const ExpensesTooltip = forwardRef(function ExpensesTooltip({ procurement, loading, getTooltipProps, getArrowProps, completed }, ref) {
    return (
        <div
            ref={ref}
            data-popper-reference-hidden="true"
            {...getTooltipProps({
                className:
                    'tw-flex tw-flex-col tw-gap-y-2 tw-bg-white tw-p-3 tw-rounded-lg tw-border-2 tw-border-charcoal-50 tw-opacity-90 tw-w-full tw-z-[999]',
                style: { maxWidth: '1140px' },
            })}
        >
            <div className="tw-overflow-auto tw-h-[calc(100%-5rem)] tw-max-h-full tw-p-0 tw-bg-transparent">
                {loading && !procurement && <LoadingSpinner />}
                {!loading && !procurement && <div>{$t('Something went wrong')}</div>}
                {!loading && procurement && (
                    <div>
                        <ProcurementExpenseTable
                            expensesTree={procurement?.expenses_tree}
                            total={completed ? procurement?.expenses_tree?.total_committed : procurement?.expenses_tree?.total}
                            maxQuantities={procurement?.expenses_tree?.max_quantities}
                            committed={completed}
                        />
                    </div>
                )}
            </div>
        </div>
    );
});
